import { Button, Card, CardContent, FormControl, Grid, InputLabel, MenuItem, Select, Toolbar, Tooltip, Typography, makeStyles } from '@material-ui/core';
import { filterNonNull } from '@zennya/web-component-library/src/components/SegmentIO';
import moment from "moment-timezone";
import { PageHeader } from 'pages/CustomerBookingCreate/components/PageHeader';
import { STEP_LOCATION, STEP_PAYMENT, STEP_SCHEDULE, STEP_SERVICE_BREAKDOWN } from 'pages/CustomerBookingCreate/constants';
import { bookingState, packagesState, servicesState, sessionTokenState, stepState, timeSlotState } from 'pages/CustomerBookingCreate/state';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useHistory } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { anonFetchSchedules } from 'services/anon.service';
import { isAuthenticated, useUserState } from 'services/authentication.service';
import { getVerifiedSchedules } from 'services/booking.service';
import { toasterror } from 'util/toasterror';
import { useTrackEvent } from 'util/trackEvent';

const useStyles = makeStyles(theme => {
  return {
    disabledSlotText: {
      textDecoration: 'line-through',
    },
    highlyAvailableSlotText: {
      color: theme.palette.primary.main,
      fontWeight: 'bold'
    },
    regularSlotText: {
      color: theme.palette.text.primary,
      fontWeight: 'regular'
    },
    availableSlotText: {
      color: theme.palette.warning.dark,
      fontWeight: 'bold'
    },
    disabledSlotIcon: {
      display: 'none'
    },
    highlyAvailableSlotIcon: {
      color: theme.palette.success.main,
    },
    regularSlotIcon: {
      color: 'transparent',
    },
  }
});
const ScheduleScreen = ({ }) => {
  const classes = useStyles()
  const [date, setDate] = useState(null);
  const [timeSlot, setTimeSlot] = useRecoilState(timeSlotState);
  const [timeSlots, setTimeSlots] = useState([]);
  const [booking, setBooking] = useRecoilState(bookingState);
  const [services, setServices] = useRecoilState(servicesState);
  const [packages, setPackages] = useRecoilState(packagesState);
  const [step, setStep] = useRecoilState(stepState);

  const [session_token, setSession_token] = useRecoilState(sessionTokenState);
  const history = useHistory()

  const [loading, setLoading] = useState(false);
  const { getUser } = useUserState()

  const [dates, setDates] = useState([]);
  const [times, setTimes] = useState([]);
  const trackEvent = useTrackEvent()

  const getStatusClass = useCallback(
    (slot) => {
      switch (slot?.status) {
        case 'NOT_AVAILABLE':
          return {
            icon: classes.disabledSlotIcon,
            toolTip: 'Not Available',
            text: classes.disabledSlotText
          }
        case 'AVAILABLE':
          return {
            icon: classes.availablerSlotIcon,
            toolTip: 'Average Provider Availability',
            text: classes.availablerSlotText
          }
        case 'HIGHLY_AVAILABLE':
          return {
            icon: classes.highlyAvailableSlotIcon,
            toolTip: "High Provider Availability",
            text: classes.highlyAvailableSlotText
          }
        default:
          break;
      }
    },
    [classes.disabledSlot, classes.highlyAvailableSlot],
  );

  useEffect(
    () => {
      if (timeSlot && timeSlots?.length > 0) {
        const fmtdate = moment(timeSlot).format('YYYY-MM-DD')
        mapTimes(fmtdate)
        setDate(fmtdate)
      }
    }, [timeSlot, timeSlots]
  );
  const mapDates = useCallback(
    (timeslots) => {
      let dts = new Set()
      timeslots?.forEach((timeslot) => {
        const dt = moment(timeslot?.start_date_time).format('YYYY-MM-DD')
        dts.add(dt)
      })
      setDates(Array.from(dts))
    },
    [],
  );

  const mapTimes = useCallback(
    (date) => {
      let tms = new Set()
      timeSlots?.forEach((timeslot) => {
        const dt = moment(timeslot?.start_date_time).format('YYYY-MM-DD')
        if (dt === date) {
          tms.add(timeslot)
        }
      })
      setTimes(Array.from(tms))
    },
    [timeSlots],
  );
  const fetchTimeslots = useCallback(
    async () => {
      try {
        setLoading(true)
        const user = await getUser()
        const params = filterNonNull({
          lat: booking?.location?.latitude,
          lng: booking?.location?.longitude,
          location_id: booking?.location?.id,
          duration: 60,
          extended_package_items: services?.map(x => x?.id),
          extended_packages: packages?.map(x => x?.id),
          packages: null,
          type: booking?.serviceCategory?.type?.id || booking?.serviceCategory?.featured_service?.type_id,
          should_check_for_payment: false,
          client_type: 'web',
          should_check_for_medical_id: false,
          should_check_high_availability: true,
          "white_label_customer": booking?.white_label_customer?.id,
        })
        let res = null
        if (isAuthenticated()) {
          res = await getVerifiedSchedules(user?.client?.id || user?.user?.id, params)
        } else {
          res = await anonFetchSchedules(session_token, params)
        }

        if (['INVALID_LOCATION', 'TYPE_NOT_AVAILABLE'].includes(res?.code)) {
          toasterror(res?.message?.en)
          setLoading(false)
          setStep(STEP_LOCATION)
          return
        }
        setTimeSlots(res?.verified_slots)
        mapDates(res?.verified_slots)
        setLoading(false)
      } catch (error) {
        trackEvent('Create Booking Schedule - fetch timeslots error', { error: error?.message?.en || error?.response?.data?.message?.en || error?.response?.data?.details })
        toasterror(error?.message?.en || error?.response?.data?.message?.en || error?.response?.data?.details, { error })
        setLoading(false)

      }
    },
    [services, packages, booking, getUser],
  );

  useEffect(
    () => {
      fetchTimeslots()
    }, []
  );

  return (
    <>
      <PageHeader
        backStep={STEP_LOCATION}
        currentStep={STEP_SCHEDULE}
        title={"When would you like the service?"}
        subheader={"Select your preferred date and time of booking. You can only schedule up to 5 days in advance"}
        isCartEnabled={false}
        loading={loading}
      >
        <Card>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl
                  fullWidth
                  variant="outlined"
                  className={classes.formControl}
                >
                  <InputLabel id="demo-simple-select-outlined-labelDT">Date</InputLabel>
                  <Select
                    labelId="demo-simple-select-outlined-labelDT"
                    id="demo-simple-select-outlinedDT"
                    value={date}
                    onChange={(e) => {
                      trackEvent('Create Booking Schedule - select date')
                      setDate(e.target.value)
                      setTimeSlot(null)
                      mapTimes(e.target.value)
                    }}
                    label="Date"
                  >
                    {dates?.map((date, index) => (
                      <MenuItem key={index} value={date}>{moment(date).format('dddd, MMMM Do YYYY')}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {date && timeSlots?.length > 0 && (
                <Grid item xs={12} md={6}>
                  {timeSlots?.length > 0 && (
                    <FormControl
                      fullWidth
                      variant="outlined"
                      className={classes.formControl}
                    >
                      <InputLabel id="demo-simple-select-outlined-labelTM">Time</InputLabel>
                      <Select
                        labelId="demo-simple-select-outlined-labelTM"
                        id="demo-simple-select-outlinedTM"
                        value={timeSlot}
                        onChange={(e) => {
                          trackEvent('Create Booking Schedule - select time')
                          setTimeSlot(e.target.value)
                        }}
                        label="Time"
                      >
                        {times?.map((slot, idx) => {
                          const slotStyle = getStatusClass(slot)
                          return (
                            <MenuItem
                              key={idx}
                              disabled={slot?.status === 'NOT_AVAILABLE'}
                              className={classes.timeslotContainer}
                              value={slot?.start_date_time}
                            >
                              <Tooltip
                                title={slotStyle?.toolTip}
                              >
                                <Typography className={slotStyle?.text} variant="body2" color="textSecondary">
                                  {moment(slot?.start_date_time).format('LT')}
                                </Typography>
                              </Tooltip>
                            </MenuItem>
                          )
                        })}
                      </Select>
                    </FormControl>
                  )}
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>

        <Toolbar>
          <div style={{ flexGrow: 1 }} />
          <Button
            disabled={!timeSlot}
            onClick={() => {
              trackEvent('Create Booking Schedule - continue to payment')
              setStep(STEP_PAYMENT)
              history.replace(STEP_PAYMENT, { internal: true })

            }} color="primary" variant="contained" className={classes.button}>Continue</Button>
        </Toolbar>
      </PageHeader>
    </>
  )
}

export { ScheduleScreen };
